/**
 * Gráfico de linha genérico do Controle PLD (Task #190 Fase 2).
 *
 * Renderiza N séries de R$/MWh sobre um eixo X categórico (dia ou mês). Usado
 * por: Visão Geral (1 série), Comparação Submercados (4 séries diárias) e
 * Histórico (4 séries mensais). Tooltip lista cada série em R$/MWh.
 *
 * Recharts: ResponsiveContainer não calcula dimensões em jsdom (testes mockam).
 */
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { fmtPld, fmtPldNum } from "./pldShared";

export interface PldSeries {
  key: string;
  label: string;
  color: string;
  /** Task #209: linha tracejada (projeção). Default sólida. */
  dashed?: boolean;
}

/** Task #209: banda de incerteza (P10–P90). Cada `points[key]` = [low, high]
 * (range area) ou null nos pontos sem banda. */
export interface PldBand {
  key: string;
  color: string;
  label?: string;
}

export type PldChartPoint = Record<string, string | number | number[] | null>;

interface Props {
  points: PldChartPoint[];
  xKey: string;
  series: PldSeries[];
  band?: PldBand;
  height?: number;
  testId?: string;
}

interface TooltipEntry {
  dataKey: string;
  value: number | number[] | null;
  color: string;
  name: string;
}

function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  // Task #209: a banda (value = [p10, p90]) não vira linha no tooltip.
  const linhas = payload.filter((e) => !Array.isArray(e.value));
  if (linhas.length === 0) return null;
  return (
    <div
      className="bg-indra-azul-deep border border-white/10 rounded-md p-3 text-xs text-white/90"
      data-testid="pld-chart-tooltip"
    >
      <div className="font-semibold text-white mb-1">{label}</div>
      <div className="space-y-0.5 font-mono">
        {linhas.map((e) => (
          <div key={e.dataKey} className="flex items-center gap-2">
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ backgroundColor: e.color }}
            />
            <span className="text-white/60">{e.name}</span>
            <span className="ml-auto">{fmtPld(e.value as number | null)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PldLineChart({ points, xKey, series, band, height = 300, testId }: Props) {
  return (
    <div
      className="rounded-xl border border-white/10 bg-indra-azul-surface p-4"
      data-testid={testId ?? "pld-line-chart"}
    >
      <div style={{ height }} data-testid="pld-chart-canvas">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
            <XAxis
              dataKey={xKey}
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
            />
            <YAxis
              stroke="rgba(255,255,255,0.5)"
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: "rgba(255,255,255,0.1)" }}
              tickFormatter={(v) => fmtPldNum(typeof v === "number" ? v : Number(v))}
              domain={["auto", "auto"]}
              width={56}
            />
            <Tooltip content={<ChartTooltip />} />
            {series.length > 1 && (
              <Legend
                wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}
                iconType="plainline"
              />
            )}
            {/* Banda P10–P90 (range area): renderizada ANTES das linhas pra
                ficar atrás. dataKey aponta pra um par [low, high] por ponto. */}
            {band && (
              <Area
                type="monotone"
                dataKey={band.key}
                name={band.label ?? "Banda P10–P90"}
                stroke="none"
                fill={band.color}
                fillOpacity={0.15}
                connectNulls
                isAnimationActive={false}
                legendType="none"
                activeDot={false}
              />
            )}
            {series.map((s) => (
              <Line
                key={s.key}
                type="monotone"
                dataKey={s.key}
                name={s.label}
                stroke={s.color}
                strokeWidth={2}
                strokeDasharray={s.dashed ? "5 4" : undefined}
                dot={{ r: 2, fill: s.color }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
